// «Сравнить тарифы»: Free, Plus and Premium side by side — what each plan
// includes, from the plan catalog (API). Opens as a side sheet from any
// access sheet, the payment flow or the profile; the current plan is marked
// and the next steps are trial-aware.
import { Fragment, useCallback } from 'react';
import { XButton } from '@/components/ui/Buttons';
import { Icon } from '@/components/ui/Icon';
import { useSheet } from '@/components/ui/Sheet';
import { useOpenPayment } from '@/features/profile/Billing';
import { PLAN_LABEL, useCaps, type PlanKey } from '@/hooks/useCaps';
import { usePlanCatalog } from '@/hooks/queries';
import i18n, { useT } from '@/i18n';
import { cx } from '@/lib/format';
import { ctaFor } from './Upgrade';

export function useComparePlans() {
  const { openSheet } = useSheet();
  return useCallback(() => openSheet({ kicker: i18n.t('plans:cmp.kicker'), title: i18n.t('plans:cmp.title'), body: <PlanCompare />, className: 'cmp-sheet' }), [openSheet]);
}

const ORDER: PlanKey[] = ['free', 'plus', 'premium'];

function Cell({ v }: { v: string }) {
  const { t } = useT('plans');
  if (v === '✓') return <span className="pc-v yes" aria-label={t('cmp.yes')}><Icon name="check" /></span>;
  if (v === '—') return <span className="pc-v no" aria-label={t('cmp.no')}>—</span>;
  return <span className="pc-v">{v}</span>;
}

export function PlanCompare() {
  const { t } = useT('plans');
  const cat = usePlanCatalog();
  const caps = useCaps();
  const pay = useOpenPayment();
  const mine = ORDER.indexOf(caps.plan);
  const groups = [...new Set(cat.compare.map((r) => r.group))];
  const next = ORDER.filter((_, i) => i > mine);
  return (
    <div className="pcmp">
      <div className="pcmp-grid pcmp-head">
        <span />
        {cat.plans.map((p) => (
          <div key={p.id} className={cx('pcmp-plan', p.id, p.id === caps.plan && 'mine')}>
            <b>{p.name}</b>
            <small>{p.id === 'free' ? t('cmp.free') : t('cmp.perMonth', { price: p.price })}</small>
            {p.id === caps.plan && <em>{t('cmp.mine')}</em>}
          </div>
        ))}
      </div>
      {groups.map((g) => (
        <Fragment key={g}>
          <span className="pcmp-g">{g}</span>
          {cat.compare.filter((r) => r.group === g).map((r) => (
            <div key={r.label} className="pcmp-grid pcmp-row">
              <span className="pcmp-l">{r.label}</span>
              {r.values.map((v, i) => <span key={i} className={cx('pcmp-c', ORDER[i] === caps.plan && 'mine')}><Cell v={v} /></span>)}
            </div>
          ))}
        </Fragment>
      ))}
      <p className="up-note">{cat.trial}</p>
      {next.length > 0 && (
        <div className="up-acts pcmp-acts">
          {next.map((p) => {
            const cta = ctaFor(p, caps);
            return <XButton key={p} inv label={cta.kind === 'trial' ? cta.label : t(caps.plan === 'free' ? 'cta.subscribe' : 'cta.switchTo', { plan: PLAN_LABEL[p] })}
              onClick={() => pay({ mode: caps.plan === 'free' ? 'new' : 'change', plan: p === 'premium' ? 'premium' : 'plus' })} />;
          })}
        </div>
      )}
    </div>
  );
}
